import { useEffect, useMemo } from 'react'
import { useCallback, useState, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'

import {
  getCalculatorProperties,
  getCalculatorPropertiesKey,
  calculatePayment,
  calculatePaymentKey,
} from '../../../api-client/purchase'

import Tabs from '../../../components/tabs'
import { MAX_AGE_OF_VEHICLE_FOR_LEASE } from '../../../constants'
import { now } from '../../../utils/date-service'
import useVehiclePageData from '../use-vehicle-page-data'
import Calculator from './calculator'

function objectFlip(object) {
  const flipped = {}

  Object.keys(object).forEach((key) => {
    flipped[object[key]] = key
  })
  return flipped
}

const initialPaymentCombination = {
  term: 60,
  downPayment: 0,
  interestRate: 5.9,
  paymentType: '',
  paymentFrequency: '',
}

const initialRates = {
  monthlyPayment: 'N/A',
  totalInterest: 'N/A',
  totalPayment: 'N/A',
}

const minSupportedLeaseYear = now().getFullYear() - MAX_AGE_OF_VEHICLE_FOR_LEASE

export default function Payment() {
  const calculationScroll = useRef(null)

  const { vehicleData, auctionInfo } = useVehiclePageData()

  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const [leaseCombination, setLeaseCombination] = useState({
    ...initialPaymentCombination,
    vehicleId: vehicleData.id,
  })
  const [financeCombination, setFinanceCombination] = useState({
    ...initialPaymentCombination,
    vehicleId: vehicleData.id,
  })

  const { data: calculatorProperties = {} } = useQuery({
    queryKey: getCalculatorPropertiesKey,
    queryFn: getCalculatorProperties,
    onSuccess: (data) => {
      setFinanceCombination((prev) => ({
        ...prev,
        paymentType: objectFlip(data.paymentTypes)?.Loan,
        paymentFrequency: objectFlip(data.paymentFrequencies)?.Monthly,
      }))
      setLeaseCombination((prev) => ({
        ...prev,
        paymentType: objectFlip(data.paymentTypes)?.Lease,
        paymentFrequency: objectFlip(data.paymentFrequencies)?.Monthly,
      }))
    },
  })

  const { data: leaseRates = initialRates, isLoading: isLeaseLoading } = useQuery({
    queryKey: [
      calculatePaymentKey,
      leaseCombination,
      auctionInfo?.topBidAmount,
      vehicleData?.desiredBuyNowPrice,
    ],
    queryFn: () =>
      calculatePayment({
        ...leaseCombination,
        purchase: auctionInfo?.topBidAmount || vehicleData?.desiredBuyNowPrice,
      }),
    enabled:
      vehicleData.year >= minSupportedLeaseYear &&
      leaseCombination.paymentFrequency !== '' &&
      leaseCombination.paymentType !== '',
  })
  const { data: financeRates = initialRates, isLoading: isFinanceLoading } = useQuery({
    queryKey: [
      calculatePaymentKey,
      financeCombination,
      auctionInfo?.topBidAmount,
      vehicleData?.desiredBuyNowPrice,
    ],
    queryFn: () =>
      calculatePayment({
        ...financeCombination,
        purchase: auctionInfo?.topBidAmount || vehicleData?.desiredBuyNowPrice,
      }),
    enabled: leaseCombination.paymentFrequency !== '' && leaseCombination.paymentType !== '',
  })

  const handleCalculateLease = useCallback(
    (form) => {
      setLeaseCombination({
        ...form,
        vehicleId: vehicleData.id,
        paymentType: objectFlip(calculatorProperties.paymentTypes)?.Lease,
      })
      calculationScroll.current.scrollIntoView()
    },
    [vehicleData, calculatorProperties],
  )

  const handleCalculateFinance = useCallback(
    (form) => {
      setFinanceCombination({
        ...form,
        vehicleId: vehicleData.id,
        paymentType: objectFlip(calculatorProperties.paymentTypes)?.Loan,
      })
      calculationScroll.current.scrollIntoView()
    },
    [vehicleData, calculatorProperties],
  )

  const tabs = useMemo(() => ['Finance'], [])
  useEffect(() => {
    if (vehicleData.year >= minSupportedLeaseYear) {
      tabs.push('Lease')
    }
  }, [tabs, vehicleData.year])

  return (
    <>
      <div className='paymentContainerWrapper flex justifyCenter flexDirectionColumn'>
        <h2 className='paymentHeading' ref={calculationScroll}>
          Payment
        </h2>
        <div className='tabsWrapper'>
          <Tabs
            activeIndex={activeTabIndex}
            onChange={(newIndex) => setActiveTabIndex(newIndex)}
            tabs={tabs}
          />
        </div>

        {activeTabIndex === 0 && (
          <Calculator
            type='finance'
            price={auctionInfo?.topBidAmount || vehicleData.price}
            properties={calculatorProperties}
            rates={financeRates}
            onPress={handleCalculateFinance}
          />
        )}
        {activeTabIndex === 1 && (
          <Calculator
            type='Lease'
            disabled={isFinanceLoading || isLeaseLoading}
            price={auctionInfo?.topBidAmount || vehicleData.price}
            properties={calculatorProperties}
            rates={leaseRates}
            onPress={handleCalculateLease}
          />
        )}
      </div>

      <style jsx>{`
        .paymentHeading {
          line-height: 4.3rem;
          font-weight: 700;
          font-size: 3.2rem;
          margin: 5.2rem 0 2rem;
        }

        .tabsWrapper {
          align-self: center;
          margin: 0 0 4.4rem;
          max-width: ${tabs?.length > 1 ? '90rem' : '45rem'};
          width: 100%;
        }

        @media (max-width: 1024px) {
          .paymentHeading {
            line-height: 2.7rem;
            font-size: 2rem;
            padding: 5.2rem 0 2rem;
            width: 100%;
            margin: 0 auto;
          }
        }
      `}</style>
    </>
  )
}
